import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  Columns3,
  Database,
  Download,
  Eye,
  FileArchive,
  FileJson,
  FileUp,
  Filter,
  FolderOpen,
  Gauge,
  GitMerge,
  GitCompareArrows,
  Grid3X3,
  HardDrive,
  Info,
  Keyboard,
  Layers3,
  Loader2,
  Menu,
  Moon,
  PanelRight,
  Play,
  Plus,
  Radar,
  RefreshCw,
  Save,
  Search,
  Scissors,
  Send,
  Settings,
  ShieldCheck,
  SkipBack,
  SkipForward,
  Sparkles,
  Square,
  Tags,
  UploadCloud,
  Undo2,
  Wand2,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AuraTelemetryEventLog, ProoflineStatus } from "@auraone/aura-ide-kit";
import { commands, datasets, probeTrials } from "./data";
import {
  buildVirtualWindow,
  clampTime,
  defaultFilters,
  defaultSort,
  exportManifest,
  formatTime,
  nextSuccessState,
  readinessTone,
  summarizeDataset,
  visibleEpisodes,
} from "./core";
import {
  artifactPlan,
  buildDeterministicClusters,
  buildLocalEvidenceArchive,
  buildProbeReport,
  buildSensorQaReport,
  buildSensorQaMarkdown,
  createDatasetFromLocalFiles,
  downloadBinaryArtifact,
  downloadTextArtifact,
  mergeClusterRows,
  recomputeDeterministicClusters,
  refreshClusterRows,
  splitClusterRows,
  summarizeClusterRows,
  type ExportScope,
  type LocalFileEntry,
} from "./localOperations";
import {
  LocalDiagnosticEventBuffer,
  ROBOTICS_PLATFORM_HOOKS,
  createRoboticsDiagnosticEvent,
  ensureRoboticsIntakeInstallSigningKeypair,
  roboticsIntakeInstallSigningKeypairKey,
  toAuraTelemetryEvents,
  type LocalDiagnosticEntry,
} from "./platformContracts";
import type {
  Cluster,
  CommandId,
  DatasetTab,
  Density,
  Episode,
  ExportTarget,
  FilterState,
  Intervention,
  SensorStream,
  SortField,
  SortState,
  ViewMode,
} from "./types";
import type { OperationState } from "./types";

import {
  Chip,
  DatasetProvenanceNote,
  Metric,
  Sparkline,
  StateCard,
  StateLine,
} from "./components";
import { SensorPreview, ViewContext } from "./views-browse";
import type { ViewBody } from "./views-release";
export function ClusterView({
  dataset,
  chooseEpisode,
  clusterRows,
  clusterRunState,
  clusterProgress,
  runClustering,
  splitCluster,
  mergeCluster,
}: Pick<
  Parameters<typeof ViewBody>[0],
  | "dataset"
  | "chooseEpisode"
  | "clusterRows"
  | "clusterRunState"
  | "clusterProgress"
  | "runClustering"
  | "splitCluster"
  | "mergeCluster"
>) {
  const episodes = dataset.episodes;
  const aggregate = summarizeClusterRows(clusterRows, episodes);
  const synthetic = dataset.provenance.kind === "repository-synthetic-fixture";
  return (
    <section
      className="surface cluster-surface"
      aria-label="Failure intelligence"
      tabIndex={0}
    >
      <div className="section-head">
        <div>
          <h1>Failure intelligence</h1>
          <p>
            Group explicit failure fields locally, then review, split, merge,
            and reverse deterministic changes.
          </p>
        </div>
        <button
          aria-label={
            clusterRunState === "running"
              ? `Clustering ${clusterProgress}%`
              : "Run clustering"
          }
          onClick={runClustering}
          disabled={clusterRunState === "running"}
        >
          {clusterRunState === "running" ? (
            <Loader2 className="spin" size={16} />
          ) : (
            <Wand2 size={16} />
          )}
          <span className="cluster-run-long">
            {clusterRunState === "running"
              ? `Clustering ${clusterProgress}%`
              : "Run clustering"}
          </span>
          <span className="cluster-run-short">
            {clusterRunState === "running" ? `${clusterProgress}%` : "Run"}
          </span>
        </button>
      </div>
      <DatasetProvenanceNote
        dataset={dataset}
        context="Deterministic local clustering uses only fields present in the active records."
      />
      {clusterRunState === "running" ? (
        <progress
          className="operation-progress"
          value={clusterProgress}
          max={100}
          aria-label="Clustering progress"
        />
      ) : null}
      <div className="cluster-list">
        {!clusterRows.length ? (
          <StateCard
            icon={Radar}
            title="No declared failure clusters"
            text="The active dataset has no explicit failure outcome, failure-cluster field, or failure taxonomy tag. Run clustering after importing those fields."
          />
        ) : null}
        {clusterRows.map((cluster) => {
          const representative =
            episodes.find(
              (episode) => episode.id === cluster.representativeEpisodeId,
            ) ?? episodes[0];
          if (!representative) return null;
          return (
            <article
              key={cluster.id}
              className="cluster-row"
              data-testid="failure-card"
            >
              <SensorPreview
                size="banner"
                badge={{
                  tone:
                    cluster.trainingDecision === "exclude from training"
                      ? "fail"
                      : readinessTone(cluster.readiness),
                  label: `${cluster.size} ep`,
                }}
                synthetic={synthetic}
              />
              <div className="cluster-summary">
                <span className="cluster-id">
                  {cluster.id} ·{" "}
                  {representative.embodiment ?? "embodiment unknown"}
                </span>
                <h2>{cluster.title}</h2>
                <p>{cluster.dominantTag ?? "Dominant taxonomy tag unknown"}</p>
              </div>
              <dl className="cluster-metrics">
                <div>
                  <dt>Episodes</dt>
                  <dd className="numeric">{cluster.size}</dd>
                </div>
                <div>
                  <dt>Readiness</dt>
                  <dd
                    className={`${typeof cluster.readiness === "number" ? "numeric " : ""}tone-${readinessTone(cluster.readiness)}`}
                  >
                    {cluster.readiness ?? "Unknown"}
                  </dd>
                </div>
                <div>
                  <dt>Homogeneity</dt>
                  <dd
                    className={
                      typeof cluster.homogeneity === "number"
                        ? "numeric"
                        : undefined
                    }
                  >
                    {cluster.homogeneity ?? "Unknown"}
                  </dd>
                </div>
              </dl>
              <div className="cluster-decision">
                <Chip
                  label={cluster.trainingDecision}
                  tone={
                    cluster.trainingDecision === "exclude from training"
                      ? "fail"
                      : cluster.trainingDecision === "training-ready"
                        ? "pass"
                        : cluster.trainingDecision === "unknown"
                          ? "neutral"
                          : "warn"
                  }
                />
                <div className="row-actions">
                  <button
                    className="primary cluster-review-action"
                    onClick={() => chooseEpisode(representative)}
                  >
                    <Eye size={15} />
                    <span className="cluster-review-long">Review evidence</span>
                    <span className="cluster-review-short">Review</span>
                  </button>
                  <button
                    className="tertiary"
                    aria-label={`Split ${cluster.title} cluster`}
                    onClick={() => splitCluster(cluster.id)}
                  >
                    <Scissors size={15} />
                    <span>Split</span>
                  </button>
                  <button
                    className="tertiary"
                    aria-label={`Merge ${cluster.title} cluster`}
                    onClick={() => mergeCluster(cluster.id)}
                    disabled={clusterRows.length < 2}
                    title={
                      clusterRows.length < 2
                        ? "A second cluster is required before merging."
                        : "Merge with the nearest comparison cluster"
                    }
                  >
                    <GitMerge size={15} />
                    <span>Merge</span>
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
      <div className="dashboard-row">
        <Metric
          label="Recovery success"
          value={
            aggregate.recoverySuccess === null
              ? "Unknown"
              : `${aggregate.recoverySuccess}%`
          }
          tone={
            aggregate.recoverySuccess === null
              ? "neutral"
              : aggregate.recoverySuccess >= 75
                ? "pass"
                : "warn"
          }
        />
        <Metric
          label="Median time to intervention"
          value={
            aggregate.medianTimeToIntervention === null
              ? "Unknown"
              : `${aggregate.medianTimeToIntervention}s`
          }
          tone={
            aggregate.medianTimeToIntervention === null ? "neutral" : "warn"
          }
        />
        <Metric
          label="Cluster homogeneity"
          value={
            aggregate.clusterHomogeneity === null
              ? "Unknown"
              : aggregate.clusterHomogeneity.toFixed(2)
          }
          tone={
            aggregate.clusterHomogeneity === null
              ? "neutral"
              : aggregate.clusterHomogeneity >= 0.75
                ? "pass"
                : "warn"
          }
        />
      </div>
      <details className="accessible-data">
        <summary>Failure cluster records</summary>
        <div
          className="table-scroll"
          role="region"
          aria-label="Failure cluster table"
          tabIndex={0}
        >
          <table>
            <thead>
              <tr>
                <th>Cluster</th>
                <th>Episodes</th>
                <th>Dominant tag</th>
                <th>Readiness</th>
                <th>Decision</th>
              </tr>
            </thead>
            <tbody>
              {clusterRows.map((cluster) => (
                <tr key={cluster.id}>
                  <td>{cluster.title}</td>
                  <td>{cluster.size}</td>
                  <td>{cluster.dominantTag ?? "Unknown"}</td>
                  <td>{cluster.readiness ?? "Unknown"}</td>
                  <td>{cluster.trainingDecision}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}


export function CompareView({
  dataset,
  selectedEpisode,
  peers,
}: {
  dataset: DatasetTab;
  selectedEpisode: Episode;
  peers: Episode[];
}) {
  const compared = [selectedEpisode, ...peers.slice(0, 1)];
  const synthetic = dataset.provenance.kind === "repository-synthetic-fixture";
  return (
    <section
      className="surface"
      aria-label="Episode metadata compare"
      tabIndex={0}
    >
      <div className="section-head">
        <div>
          <h1>Compare</h1>
          <p>
            Compare declared episode metadata and available sensor samples
            without inferring missing model or simulation evidence.
          </p>
        </div>
        <button
          disabled
          title="Model-input mapping requires an adapter plugin that is not connected in this source build."
        >
          <Columns3 size={16} /> Map model inputs
        </button>
      </div>
      <div className="compare-grid">
        {compared.map((episode, index) => {
          const sampledSensor = episode.sensors.find(
            (sensor) => sensor.samples.length,
          );
          return (
            <article key={episode.id} className="compare-pane">
              <SensorPreview
                size="compare"
                embodiment={episode.embodiment}
                badge={{
                  tone: index === 0 ? "pass" : "warn",
                  label: index === 0 ? "reference" : "candidate",
                }}
                foot={{
                  label: episode.task?.replaceAll("_", " ") ?? "Task unknown",
                  meta: formatTime(episode.duration),
                }}
                synthetic={synthetic}
              />
              <h2>{episode.id}</h2>
              {sampledSensor ? (
                <Sparkline
                  values={sampledSensor.samples}
                  accentKey={sampledSensor.kind}
                  fallbackIndex={index}
                />
              ) : (
                <p className="telemetry-unknown">
                  No sensor samples were declared.
                </p>
              )}
            </article>
          );
        })}
      </div>
      <div
        className="table-scroll"
        role="region"
        aria-label="Compared episode fields"
        tabIndex={0}
      >
        <table>
          <thead>
            <tr>
              <th>Field</th>
              {compared.map((episode) => (
                <th key={episode.id}>{episode.id}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>Task</th>
              {compared.map((episode) => (
                <td key={episode.id}>{episode.task ?? "Unknown"}</td>
              ))}
            </tr>
            <tr>
              <th>Duration</th>
              {compared.map((episode) => (
                <td key={episode.id}>{formatTime(episode.duration)}</td>
              ))}
            </tr>
            <tr>
              <th>Frame rate</th>
              {compared.map((episode) => (
                <td key={episode.id}>
                  {episode.frameRateHz === null
                    ? "Unknown"
                    : `${episode.frameRateHz} Hz`}
                </td>
              ))}
            </tr>
            <tr>
              <th>Readiness</th>
              {compared.map((episode) => (
                <td key={episode.id}>{episode.readiness ?? "Unknown"}</td>
              ))}
            </tr>
            <tr>
              <th>Sensor QA</th>
              {compared.map((episode) => (
                <td key={episode.id}>{episode.sensorQaStatus}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}


export function ProbeView({ dataset }: { dataset: DatasetTab }) {
  const summary = summarizeDataset(dataset);
  const [policy, setPolicy] = useState("mock");
  const [trialCount, setTrialCount] = useState(4);
  const [probeState, setProbeState] = useState<OperationState>("idle");
  const [progress, setProgress] = useState(0);
  const [report, setReport] = useState<ReturnType<
    typeof buildProbeReport
  > | null>(null);
  const visibleTrials = report?.trials ?? probeTrials.slice(0, trialCount);

  function runProbe() {
    if (policy !== "mock" || probeState === "running") return;
    setProbeState("running");
    setProgress(10);
    setReport(null);
    const interval = window.setInterval(() => {
      setProgress((current) => {
        const next = Math.min(100, current + 30);
        if (next >= 100) {
          window.clearInterval(interval);
          setReport(buildProbeReport(dataset, probeTrials, trialCount));
          setProbeState("success");
        }
        return next;
      });
    }, 120);
  }

  function downloadReport() {
    if (!report) return;
    downloadTextArtifact(
      `${dataset.name}-vla-probe.json`,
      `${JSON.stringify(report, null, 2)}\n`,
      "application/json",
    );
  }

  function downloadMarkdown() {
    if (!report) return;
    const rows = report.trials
      .map(
        (trial) =>
          `| ${trial.perturbation} | ${trial.status} | ${trial.latencyMs} ms | ${trial.description} |`,
      )
      .join("\n");
    const markdown = [
      `# Deterministic VLA probe: ${dataset.name}`,
      "",
      `Provenance: ${dataset.provenance.label}`,
      `Requested trials: ${report.requestedTrials}`,
      `Executed trials: ${report.executedTrials}`,
      "",
      "| Perturbation | Result | Latency | Description |",
      "| --- | --- | --- | --- |",
      rows,
      "",
      report.recommendation,
      "",
    ].join("\n");
    downloadTextArtifact(
      `${dataset.name}-vla-probe.md`,
      markdown,
      "text/markdown;charset=utf-8",
    );
  }

  return (
    <section className="surface" aria-label="VLA robustness probe" tabIndex={0}>
      <div className="section-head">
        <div>
          <h1>VLA robustness probe</h1>
          <p>
            Run one to four deterministic local mock perturbations against the
            active dataset metadata.
          </p>
        </div>
        <button
          onClick={runProbe}
          disabled={policy !== "mock" || probeState === "running"}
          title={
            policy !== "mock"
              ? "BYO policy adapters are not connected in this source build. Select deterministic mock."
              : undefined
          }
        >
          {probeState === "running" ? (
            <Loader2 className="spin" size={16} />
          ) : (
            <Play size={16} />
          )}
          {probeState === "running" ? `Running ${progress}%` : "Run probe"}
        </button>
      </div>
      <ViewContext dataset={dataset} summary={summary} label="Probe target" />
      <div className="probe-config">
        <label>
          Policy
          <select
            value={policy}
            onChange={(event) => setPolicy(event.target.value)}
          >
            <option value="mock">Deterministic local mock</option>
            <option value="onnx" disabled>
              ONNX unavailable
            </option>
            <option value="pytorch" disabled>
              PyTorch unavailable
            </option>
            <option value="hf" disabled>
              Hosted model unavailable
            </option>
          </select>
        </label>
        <label>
          Trials
          <input
            type="number"
            value={trialCount}
            min={1}
            max={4}
            onChange={(event) =>
              setTrialCount(
                Math.min(
                  4,
                  Math.max(1, Math.round(Number(event.target.value) || 1)),
                ),
              )
            }
          />
        </label>
        <label>
          Artifact destination
          <input value="Browser downloads" readOnly aria-readonly="true" />
        </label>
      </div>
      {policy !== "mock" ? (
        <div className="operation-note">
          <ProoflineStatus
            tone="blocked"
            label="Selected policy adapter is unavailable in this source build"
          />
        </div>
      ) : null}
      {probeState === "running" ? (
        <progress
          className="operation-progress"
          value={progress}
          max={100}
          aria-label="Probe progress"
        />
      ) : null}
      <div className="trial-list">
        {visibleTrials.map((trial) => (
          <div key={trial.id} className="trial-row">
            <Chip label={trial.perturbation} />
            <strong>{trial.description}</strong>
            <span>{trial.latencyMs ? `${trial.latencyMs} ms` : "-"}</span>
            <Chip
              label={trial.status}
              tone={
                trial.status === "fail"
                  ? "fail"
                  : trial.status === "pass"
                    ? "pass"
                    : "warn"
              }
            />
          </div>
        ))}
      </div>
      <article className="report-preview">
        <h2>Inline report</h2>
        <p>
          {report?.recommendation ??
            "Run the deterministic mock probe to generate a local source-build result."}
        </p>
        {report ? (
          <div className="row-actions">
            <button onClick={downloadReport}>
              <FileJson size={16} /> Download JSON
            </button>
            <button onClick={downloadMarkdown}>
              <Download size={16} /> Export Markdown
            </button>
          </div>
        ) : null}
      </article>
    </section>
  );
}


export function QaView({ dataset }: { dataset: DatasetTab }) {
  const report = buildSensorQaReport(dataset);
  const summary = summarizeDataset(dataset);
  const [exported, setExported] = useState(false);
  function exportMarkdown() {
    downloadTextArtifact(
      `${dataset.name}-sensor-qa.md`,
      buildSensorQaMarkdown(dataset),
      "text/markdown;charset=utf-8",
    );
    setExported(true);
  }
  return (
    <section className="surface" aria-label="Sensor QA" tabIndex={0}>
      <div className="section-head">
        <div>
          <h1>Sensor QA</h1>
          <p>
            Compute checks only from sample rates and QA fields declared in the
            active records; missing evidence remains unknown.
          </p>
        </div>
        <button onClick={exportMarkdown}>
          <Download size={16} /> Export Markdown
        </button>
      </div>
      <ViewContext dataset={dataset} summary={summary} label="QA target" />
      <div className="qa-grid">
        {report.checks.map((check) => (
          <StateLine
            key={check.id}
            label={check.label}
            status={check.status}
            detail={check.detail}
          />
        ))}
      </div>
      <article className="report-preview">
        <h2>Metadata coverage</h2>
        <p>
          {report.sensorRecordCount} sensor records across {report.episodeCount}{" "}
          episodes were evaluated. Overall status: {report.overallStatus}.
          Provenance: {dataset.provenance.label}.
        </p>
        {exported ? (
          <ProoflineStatus tone="success" label="Markdown downloaded locally" />
        ) : null}
      </article>
    </section>
  );
}
